import React, { useState, forwardRef, useImperativeHandle, useMemo, useEffect, useRef } from "react";
import {
  Drawer,
  Button,
  Form,
  Input,
  Select,
  Collapse,
  notification,
  Checkbox,
  Divider,
  Space,
  Alert,
  Popconfirm,
  Popover,
  Tooltip
} from "antd";
import { WarningOutlined, StepForwardOutlined, InfoCircleOutlined } from '@ant-design/icons';
import AccountPayableAPI from "../../../../restAPI/AccountPayableAPI"
import AccountReceivableAPI from "../../../../restAPI/AccountReceivableAPI"
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const { Panel } = Collapse;
const { Option } = Select;

const Context = React.createContext({
  name: "Default",
});

const StepForm = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false); // Track drawer visibility
  const [currencyType, setcurrencyType] = useState([]); // for Load Currency Types
  const formRefs = useRef({}); // Store references to all form instances
  const [cancelButtonDisabled, setCancelButtonDisabled] = useState(false);
  const [submissionCancelButtonDisabled, setSubmissionCancelButtonDisabled] = useState(false);
  const [showPopconfirm, setShowPopconfirm] = useState(false);
  const [showSubmissionPopconfirm, setShowSubmissionPopconfirm] = useState(false);
  const commonFormRef = useRef();
  const [renderKey, setRenderKey] = useState(0);
  const [invoiceData, setInvoiceData] = useState([]);
  const [btnnotyfyVisible, setBtnNotyfyVisible] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [initialInvoices, setInitialInvoices] = useState([]);
  const [classification, setclassification] = useState([]);
  const [commonExRate, setCommonExRate] = useState();
  const [commonClassificationCode, setCommonClassificationCode] = useState();
  const [commonFxCurrency, setCommonFxCurrency] = useState();
  const [manuallyEditedRates, setManuallyEditedRates] = useState(new Set());
  const { token } = useSelector(state => state.auth);
  const [classificationCodes, setClassificationCodes] = useState([{ tPS_Id: "", classificationCode: "" }]);
  const [form] = Form.useForm();
  const [commonFields, setCommonFields] = useState({
    exRate: null,
    classificationCode: null,
    fxCurrency: null
  });
  const [classificationChanged, setClassificationChanged] = useState(false);

  useImperativeHandle(ref, () => ({
    openDrawer: (selectedList) => {
      setVisible(true);
      LoadInvoiceDetails(selectedList);
      setInvoiceData(selectedList)
    },
    closeDrawer: () => setVisible(false)
  }));

  useEffect(() => {
    GetDocumentClassification();
    GetCurrancyTypes();
  }, [])

  // Update this useEffect to handle all common fields
  useEffect(() => {
    const formKeys = Object.keys(formRefs.current);
    formKeys.forEach((key) => {
      const form = formRefs.current[key];
      if (form) {
        form.setFieldsValue({
          exCurrencyRate: commonExRate,
          classificationCode: commonClassificationCode,
          fxCurrency: commonFxCurrency,
        });
      }
    });
  }, [commonExRate, commonClassificationCode, commonFxCurrency]);

  // Sync all form values with the external state
  useEffect(() => {
    invoices.forEach((invoice) => {
      const form = formRefs.current[invoice.tPS_Id];
      if (form) {
        form.setFieldsValue({
          classificationCode: invoice.classificationCode,
          fxCurrency: invoice.fxCurrency,
          fxCurrencyAmount: invoice.fxCurrencyAmount,
          referenceNumber: invoice.referenceNumber,
          IsConsolidated: invoice.IsConsolidated,
          exCurrencyRate: invoice.exCurrencyRate,
          invoiceNumber: invoice.invoiceNumber,
          documentType: invoice.documentType.description,
          fxCurrency: invoice.fxCurrency,
          fxCurrencyRate: invoice.fxCurrencyRate !== null ? invoice.fxCurrencyRate : ""
        });
      }
    });
  }, [invoices]);

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description, placement) => {
    api[type]({
      message: message,
      description: description,
      placement
    });
  };

  const contextValue = useMemo(() => ({ name: "Ant Design" }), []);
  const consolidatedChange = (e, invoiceNumber) => {
    setClassificationChanged(true);
    const invoice = invoices.find((invoice) => invoice.invoiceNumber === invoiceNumber);
    const rows = []
    if (invoice) {
      const form = formRefs.current[invoice.tPS_Id];

      if (e.target.checked) {

        const classificationObj = { tPS_Id: invoice.tPS_Id, classificationCode: "004" };
        rows.push(classificationObj);
        setClassificationCodes(rows)

        form.setFieldsValue({
          classificationCode: "004",
          IsConsolidated: e.target.checked
        });
      }
      else {

        const classificationObj = { tPS_Id: invoice.tPS_Id, classificationCode: invoice.classificationCode };
        rows.push(classificationObj);
        setClassificationCodes(rows)

        form.setFieldsValue({
          classificationCode: invoice.classificationCode,
          IsConsolidated: false
        });

      }
    }

  }

  const GetDocumentClassification = async () => {
    await AccountPayableAPI.GetDocumentClassification(
      token,
      localStorage.getItem('groupId'),
      localStorage.getItem('companyId')
    )
      .then((res) => {
        if (res?.data) {
          const classification = res.data.map((rowValue) => ({
            value: rowValue.code,
            label: `${rowValue.code} - ${rowValue.description}`, // string for search
            code: rowValue.code,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
  
          const valuesToRemove = ['009', '033', '034', '035', '036', '037', '045'];
          const ARClassificationCodes = classification.filter(
            (item) => !valuesToRemove.includes(String(item.value))
          );
  
          setclassification(ARClassificationCodes);
        }
      })
      .catch((err) => {
        const message = err.response?.data?.message || err.message;
        openNotificationWithIcon('error', 'No Document Type found', message, 'bottomLeft');
      });
  };
  

  const GetCurrancyTypes = async () => {
    await AccountPayableAPI.GetCurrancyTypes(token)
      .then((res) => {
        if (res?.data) {
          const CurrancyTypes = res.data.map((rowValue) => ({
            value: rowValue.code,
            label: rowValue.currency
          })).sort((a, b) => a.label.localeCompare(b.label));
          setcurrencyType(CurrancyTypes);
        }
      })
      .catch((err) => {
        if (err.response) {
          openNotificationWithIcon('error', 'No Currancy Type found', `${err.response.data.message}`, 'bottomLeft');
        } else {
          openNotificationWithIcon('error', 'No Currancy Type found', `${err.message}`, 'bottomLeft');
        }
      });
  }

  const LoadInvoiceDetails = async (invoiceList) => {
    const formData = {
      invoiceNumbers: invoiceList,
      transactionType: "Accounts receivable",
      companyId: localStorage.getItem("companyId")
    };
    await AccountPayableAPI.GetInvoiceDetails(token, localStorage.getItem("groupId"), localStorage.getItem("companyId"), formData)
      .then((res) => {
        if (res?.data) {;
          // this will return duplicate data as well so we need to remove the duplicates
          const invoicDetails = removeDuplicates(res.data);
          const rows = [];
          invoicDetails.map((rowValue) => {
            rows.push(
              {
                mOGU_UUID: rowValue.MOGU_UUID,
                tPS_Id: rowValue.TPS_Id,
                invoiceNumber: rowValue.invoiceNumber,
                documentType: { "code": rowValue.documentType.code, "description": rowValue.documentType.description },
                classificationCode: rowValue.classificationCode,
                fxCurrency: rowValue.fxCurrency,
                fxCurrencyAmount: rowValue.fxCurrencyAmount,
                fxCurrencyRate: rowValue.fxCurrencyRate !== null ? rowValue.fxCurrencyRate : "",
                exCurrencyRate: rowValue.fxCurrencyRate != "null" ? rowValue.fxCurrencyRate : "",
                referenceNumber: "",
                IsConsolidated: (rowValue.classificationCode === "004") ? true : false,
                isTradingPartnerValid: rowValue?.isTradingPartnerValid,
                isFailedInvoice: rowValue?.isFailedInvoice,
              }
            )
          });
          setInitialInvoices(rows);
          setInvoices(rows);
        }
      })
      .catch((err) => {
        if (err.response) {
          openNotificationWithIcon('error', 'No Invoice Details found', `${err.response.data.message}`, 'bottomLeft');
        } else {
          openNotificationWithIcon('error', 'Invoice Details Processing Error', `${err.message}`, 'bottomLeft');
        }
      });
  }

  const removeDuplicates = (arr) => {
    const seen = new Set(); // Track seen IDs
    return arr.filter((item) => {
      if (!seen.has(item.invoiceNumber)) {
        seen.add(item.invoiceNumber);
        return true;
      }
      return false; // Discard duplicates
    });
  };

  const openNotification = (placement) => {
    api.success({
      message: `Success`,
      description: (
        <Context.Consumer>
          {({ name }) => `Successfully Saved`}
        </Context.Consumer>
      ),
      placement,
    });
  };

  // Helper function to update all forms
  const updateAllForms = (fieldName, value) => {
    const formKeys = Object.keys(formRefs.current);
    formKeys.forEach((key) => {
      const form = formRefs.current[key];
      if (form) {
        form.setFieldsValue({
          [fieldName]: value
        });
      }
    });

    // Also update the invoices state
    setInvoices(prev =>
      prev.map(item => ({
        ...item,
        [fieldName]: value
      }))
    );
  };

  const NotifyAdmin = async () => {
    //send email to the admin about the failed reasons responce : "message": "Notification sent to system admins with unique attachments."
    let notificationList = invoices.reduce((acc, invoice) => {
      if (invoice.TPS_Id) {
        acc.push({
          moguUUID: invoice.MOGU_UUID,
          tpsID: invoice.TPS_Id,
          invoiceNumber: invoice.invoiceNumber,
          reason: "Data extraction encountered an issue. Please verify the document and reupload to proceed."
        });
      }
      return acc;
    }, []);
    const notifyadminpayload = {
      groupId: localStorage.getItem("groupId"),
      companyId: localStorage.getItem("companyId"),
      failedInvoices: notificationList
    };
    await AccountPayableAPI.NotifyErrorAdminViaEmail(token, localStorage.getItem("groupId"), localStorage.getItem("companyId"), notifyadminpayload)
      .then((response) => {
        openNotificationWithIcon("success", "Successful", `${response.message}`, "bottomLeft");
        setBtnNotyfyVisible(false);
      })
      .catch((err) => {
        if (err.response) {
          openNotificationWithIcon("error", "Notify Admin  failed", `${err.response.data.message}`, "bottomLeft");
        } else {
          openNotificationWithIcon("error", "Notify Admin  failed", `${err.message}`, "bottomLeft");
        }
      });
  }

  const onCommonClassificationChange = (value) => {
    setClassificationChanged(true);
    setCommonClassificationCode(value);
    // Update forms immediately
    const formKeys = Object.keys(formRefs.current);
    formKeys.forEach((key) => {
      const form = formRefs.current[key];
      if (form) {
        form.setFieldsValue({
          classificationCode: value
        });
      }
    });
    updateAllForms('classificationCode', value);
  };

  const onCommonCurrrancyChanged = (value) => {

    setCommonFxCurrency(value);

    const formKeys = Object.keys(formRefs.current);
    formKeys.forEach((key) => {
      const form = formRefs.current[key];
      if (form) {
        form.setFieldsValue({
          fxCurrency: value
        });
      }
    });
    updateAllForms('fxCurrency', value);
  }

  const onChangeCommonExchangeRate = (e) => {
    setCommonExRate(e.target.value);

    setInvoices(prev =>
      prev.map(item => ({
        ...item,
        exCurrencyRate: e.target.value
      }))
    );
    updateAllForms('exCurrencyRate', e.target.value);
  };

  const redirectToTradingPartner = () => {
    navigate("/masterdata/tradingpartners");
  }

  const handleExRateKeyDown = (e) => {
    if (!/^\d$/.test(e.key) && e.key !== '.' && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const onClassificationChange = (value, InvoiceNo) => {
    setClassificationChanged(true);
    setInvoices((prev) =>
      prev.map((item) =>
        item.invoiceNumber == InvoiceNo ? { ...item, ["classificationCode"]: value, ["IsConsolidated"]: value == "004" ? true : false } : item
      )
    );
  };

  const onCurrrancyChanged = (value, InvoiceNo) => {
    setInvoices((prev) =>
      prev.map((item) =>
        item.invoiceNumber == InvoiceNo ? { ...item, ["fxCurrency"]: value } : item
      ));
  }

  const onChangeExchangeRate = (e, InvoiceNo) => {
    setInvoices(prev =>
      prev.map(item =>
        item.invoiceNumber === InvoiceNo
          ? { ...item, exCurrencyRate: e.target.value }
          : item
      )
    );
  }

  const onSubmissionPopConfirm = () => {
    setShowSubmissionPopconfirm(true);
  }

  // Function to handle the common submit button click
  const handleSubmit = async () => {
    try {
      setShowSubmissionPopconfirm(false);
      const invoiceFormKeys = invoices.map(invoice => invoice.tPS_Id);

      // Validate all invoice forms
      const results = await Promise.all(
        invoiceFormKeys.map(key =>
          formRefs.current[key]?.validateFields()
        )
      );

      const payload = [];
      invoices.map((rowValue) => {
        if (!rowValue.isFailedInvoice) {
          let invoiceClassificationCode = "";
          classificationCodes.map((item) => {
            if (item.tPS_Id === rowValue.tPS_Id) {
              invoiceClassificationCode = item.classificationCode;
            }
          })

          payload.push({
            MOGU_UUID: rowValue.mOGU_UUID,
            TPS_Id: rowValue.tPS_Id,
            transactionType: "Accounts receivable",
            classificationChanged: classificationChanged,
            submitting_items: {
              invoiceNumber: rowValue.invoiceNumber,
              documentType: rowValue?.documentType?.code,
              classificationCode: invoiceClassificationCode === "" ? rowValue?.classificationCode : invoiceClassificationCode,
              fxCurrency: rowValue?.fxCurrency,
              fxCurrencyAmount: rowValue?.exCurrencyRate,
            },
            companyId: localStorage.getItem("companyId"),
            groupId: localStorage.getItem("groupId"),
          });
        }
      });

      await AccountReceivableAPI.InvoiceSubmition(
        token,
        localStorage.getItem('groupId'),
        localStorage.getItem('companyId'),
        payload
      )
        .then((response) => {
          openNotificationWithIcon(
            "success",
            "Successful",
            `${response.message}`,
            "bottomLeft"
          );
          setVisible(false);
        })
        .catch((err) => {
          if (err.response) {
            openNotificationWithIcon(
              "error",
              "Invoice Submission failed",
              `${err.response.data.message}`,
              "bottomLeft"
            );

          } else {
            openNotificationWithIcon(
              "error",
              "Invoice Submission failed",
              `${err.message}`,
              "bottomLeft"
            );

          }
        });
    } catch (errors) {
      // If any form is invalid, show error messages
      console.log('Validation errors:', errors);
    }
  };

  const handleConfirm = async () => {
    NotifyAdmin();
  };

  const handleCancel = () => {
    setCancelButtonDisabled(true);
    openNotificationWithIcon(
      "error",
      "Abort",
      `Notify admin cancelled`,
      "bottomLeft"
    );

    setTimeout(() => {
      setShowPopconfirm(false);
      setCancelButtonDisabled(false);
    }, 3000);
  };

  const handleAbortSubmissionConfirm = () => {
    setSubmissionCancelButtonDisabled(true);
    openNotificationWithIcon(
      "error",
      "Abort",
      `Invoice submission cancelled`,
      "bottomLeft"
    );

    setTimeout(() => {
      setShowSubmissionPopconfirm(false);
      setSubmissionCancelButtonDisabled(false);
    }, 3000);
  };


  const handleReset = () => {
    setInvoices(initialInvoices);

    // Reset individual forms to their initial values
    Object.keys(formRefs.current).forEach(key => {
      const form = formRefs.current[key];
      if (form) {
        const initialInvoice = initialInvoices.find(inv => inv.tPS_Id === key);
        if (initialInvoice) {
          form.setFieldsValue({
            invoiceNumber: initialInvoice.invoiceNumber,
            documentType: initialInvoice.documentType.description || "No Document type Received",
            classificationCode: initialInvoice.classificationCode,
            fxCurrency: initialInvoice.fxCurrency,
            IsConsolidated: initialInvoice.IsConsolidated,
            referenceNumber: (initialInvoice.documentType.description !== "Invoice") ?
              initialInvoice.referenceNumber : "N/A",
            exCurrencyRate: initialInvoice.exCurrencyRate
          });
        }
      }
    });

    if (commonFormRef.current) {
      commonFormRef.current.resetFields();
    }
  };

  const popoverContentCC = (
    <div>
      For PDF submissions: The classification code is automatically set based on the trading<br />
      partner or company classification code selected during setup. If you need to use a different<br />
      code, update it here.<br /><br />
      For Excel submissions: Classification codes are taken from the Excel template. If you need <br />
      to use a different code, update it here. Updating the code here will override all line item <br />
      classification codes in the submission.
    </div>
  );

  return (
    <Context.Provider value={contextValue}>
      {contextHolder}
      <Drawer
        title="e-Invoice Submission"
        width={700}
        visible={visible}
        onClose={() => {
          setVisible(false);
          setShowSubmissionPopconfirm(false);
          setCancelButtonDisabled(true);
          setTimeout(() => {
            setShowPopconfirm(false);
            setCancelButtonDisabled(false);
          }, 3000);
        }}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button style={{ marginRight: 8 }} size="large" onClick={() => handleReset()}>
              Reset
            </Button>

            <Tooltip
              title={
                invoices.some(item => item.isTradingPartnerValid === false)
                  ? "Submission blocked: One or more invoices have an invalid trading partner."
                  : invoices.every(item => item.isFailedInvoice)
                  ? "Submission blocked: All invoices failed. Please re-upload."
                  : ""
              }
            >
            <span>
            <Popconfirm
              title={`Please confirm the classification codes before submitting?`}
              open={showSubmissionPopconfirm}
              onConfirm={handleSubmit}
              onCancel={handleAbortSubmissionConfirm}
              okText="Confirm"
              cancelText="Cancel"
              cancelButtonProps={{ disabled: submissionCancelButtonDisabled }}
            >
              <Button
                type="primary"
                size="large"
                onClick={onSubmissionPopConfirm}
                disabled={(invoices.every(item => item.isFailedInvoice)) || (invoices.some(item => item.isTradingPartnerValid === false))}
              >
                Submit
              </Button>
            </Popconfirm>
            </span>
            </Tooltip>
          </div>
        }
        forceRender
      >
        <div style={{ marginTop: 24 }}>
          {invoices.length > 1 && (<>
            <Divider style={{ borderColor: '#7cb305' }}>Common Update Fields</Divider>
            <Form name="commonProperties" layout="vertical" ref={commonFormRef} >
              <Form.Item
                label={
                  <span>
                    Update Classification Code{' '}
                    <Popover content={popoverContentCC} title="Classification Info" trigger="hover">
                      <InfoCircleOutlined className="info-icon" />
                    </Popover>
                  </span>
                }
                name="commonclassificationCode"
              >
                <Select
                  showSearch                                  // enable search
                  size="large"
                  options={classification}                    // label must be string
                  onSelect={(value) => { onCommonClassificationChange(value) }}
                  placeholder="Please Select Classification Code"
                  optionFilterProp="label"                    // search uses label
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }                                           // case-insensitive search
                  optionRender={(option) => (                 // optional: styled dropdown items
                    <div style={{ whiteSpace: 'normal', lineHeight: '1.2em', overflowWrap: 'break-word' }}>
                      {option.data.label}
                    </div>
                  )}
                />
              </Form.Item>

              {/* FX Currency */}
              <Form.Item label="FX Currency" name="commonfxCurrency">
                <Select size="large" value={commonFxCurrency} options={currencyType} onSelect={(value) => { onCommonCurrrancyChanged(value) }} placeholder="Please Select Currency">
                </Select>
              </Form.Item>

              <Form.Item label="Exchange Currency Rate" name="commonexCurrencyRate">
                <Input size="large" value={commonExRate} onChange={onChangeCommonExchangeRate} onKeyDown={handleExRateKeyDown} placeholder="Please Enter Exchange Rate" />
              </Form.Item>
            </Form>
            <Divider style={{ borderColor: '#7cb305' }}></Divider>
          </>
          )}
          {invoices.some(item => item.isFailedInvoice) && (
            <Alert
              message="Data Submission Error"
              type="error"
              description={
                <div>
                  Data extraction encountered an issue. Please verify the document and reupload to proceed.
                  <Popconfirm
                    title={`Do you want to send an email to the system admin?`}
                    open={showPopconfirm}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    okText="Yes"
                    cancelText="No"
                    cancelButtonProps={{ disabled: cancelButtonDisabled }}
                  >
                    <Button Primary size="small" type="primary" style={{ marginTop: 8 }} disabled={!btnnotyfyVisible}>
                      Notify Admin
                    </Button>
                  </Popconfirm>
                </div>
              }
            />
          )}

          <Collapse accordion
            activeKey={invoices.length == 1 ? 0 : undefined} // Control expanded panels
            collapsible={!invoices.length == 1} // Disable collapsible behavior if only one item
          >
            {invoices.map((invoice, index) => (
              <Panel
                header={
                  <span>

                    Invoice No: {invoice.invoiceNumber}
                    <InfoCircleOutlined style={{ marginLeft: 8 }} />
                  </span>
                }
                key={index}

              >
                {/* In your form initialization, make sure to set all common values */}
                <Form
                  ref={(form) => (formRefs.current[invoice.tPS_Id] = form)}
                  layout="vertical"
                  initialValues={{
                    invoiceNumber: invoice.invoiceNumber,
                    documentType: invoice.documentType.description || "No Document type Received",
                    classificationCode: invoice.classificationCode || commonClassificationCode || "022",
                    fxCurrency: invoice.fxCurrency ?? commonFxCurrency,
                    IsConsolidated: invoice.IsConsolidated,
                    referenceNumber: (invoice.documentType.description !== "Invoice") ? invoice.referenceNumber : "N/A",
                    exCurrencyRate: invoice?.exCurrencyRate ?? commonExRate
                  }}
                >
                  {(!invoice.isTradingPartnerValid) && (<Alert
                    message="Incomplete Trading Partner"
                    description="Master data incomplete for the selected Trading Partner. Please update details to proceed."
                    type="warning"
                    action={
                      <Button Primary size="small" type="primary" onClick={redirectToTradingPartner}>
                        Go to Trading Partners
                      </Button>
                    }
                  />
                  )}
                  {/* Invoice Number */}
                  <Form.Item label="Invoice Number" name="invoiceNumber">
                    <Input size="large" disabled />
                  </Form.Item>
                  <Form.Item label="Document Type" name="documentType">
                    <Input disabled size="large" />
                  </Form.Item>

                  <Form.Item
                    label={
                      <span>
                        Update Classification Code{' '}
                        <Popover content={popoverContentCC} title="Classification Info" trigger="hover">
                          <InfoCircleOutlined className="info-icon" />
                        </Popover>
                      </span>
                    }
                    name="classificationCode"
                    rules={[
                      {
                        required: true,
                        message: "Please select the classification code!",
                      },
                    ]}
                    required>
                    <Select 
                      size="large" 
                      showSearch
                      options={classification} 
                      onSelect={(value) => { onClassificationChange(value, invoice.invoiceNumber) }} 
                      placeholder="Please Select Classification Code"
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      optionRender={(option) => (
                        <div style={{ whiteSpace: 'normal', lineHeight: '1.2em', overflowWrap: 'break-word' }}>
                          {option.data.label}
                        </div>
                      )}
                    >
                    </Select>
                  </Form.Item>

                  {/* FX Currency */}
                  <Form.Item label="FX Currency" name="fxCurrency"
                    rules={[{ required: true, message: "FX Currency Required" }]} required>
                    <Select size="large" options={currencyType} onSelect={(value) => { onCurrrancyChanged(value, invoice.invoiceNumber) }} placeholder="Please Select Currency">
                    </Select>
                  </Form.Item>

                  <Form.Item label="Exchange Currency Rate" name="exCurrencyRate"
                    rules={[{ required: true, message: "FX Currency Rate Required" }]} required>
                    <Input size="large" onChange={(e) => onChangeExchangeRate(e, invoice.invoiceNumber)} onKeyDown={handleExRateKeyDown} />
                  </Form.Item>

                  <Form.Item
                    label="" name="IsConsolidated" valuePropName="checked"
                  >
                    <Checkbox onChange={(e) => { consolidatedChange(e, invoice.invoiceNumber) }}>Consolidated</Checkbox>
                  </Form.Item>
                </Form>
              </Panel>
            ))}
          </Collapse>
        </div>
      </Drawer>
    </Context.Provider>
  );
});

export default StepForm;
